import { API, Auth } from "aws-amplify";

import * as mutations from "../../graphql/mutations";
import * as queries from "../../graphql/queries";
import * as customQueries from "../../graphql/customQueries";
import {
  UserInvitation,
  GetUserInvitationQuery,
  CreateUserInvitationInput,
  CreateUserInvitationMutation,
  ListUserInvitationsQuery,
  DeleteUserInvitationMutation,
  DeleteUserInvitationInput,
  ModelUserInvitationConnection,
  EULAText,
  SearchUserInvitationsQueryVariables,
  SearchUserInvitationsQuery,
  SearchableUserInvitationConnection,
  UserInvitationsByEmailLowerQuery,
  ValidateBulkUploadQuery,
  ValidateBulkUploadInvitation
} from "../../API";
import {
  genericQuery,
  listQueryAutoPaginated,
  searchQuery
} from "../api/apiUtils";
import { listUserInvitationsByProgramIds } from "../../queries/nongenedQueries";

export const fetchUserInvitationByEmail = async (
  email: string,
  unauthed?: boolean
) => {
  const predicate = { emailLower: email.toLowerCase() };

  const results = await listQueryAutoPaginated<
    UserInvitationsByEmailLowerQuery,
    UserInvitation,
    ModelUserInvitationConnection
  >(
    unauthed
      ? customQueries.getUserInvitationsByEmailUnauthed
      : queries.userInvitationsByEmailLower,
    "userInvitationsByEmailLower",
    "items",
    predicate,
    unauthed
  );
  if (results && results.length > 0) {
    const resultToReturn = results[0];
    return resultToReturn;
  }
  return null;
};

export const fetchAllUserInvitations = async (predicate?: object) => {
  return await listQueryAutoPaginated<
    ListUserInvitationsQuery,
    UserInvitation,
    ModelUserInvitationConnection
  >(queries.listUserInvitations, "listUserInvitations", "items", predicate);
};

export async function fetchUserInvitation(id: string, unauthed?: boolean) {
  const predicate = { id: id };
  return await genericQuery<GetUserInvitationQuery, UserInvitation>(
    unauthed
      ? customQueries.getUserInvitationUnauthed
      : queries.getUserInvitation,
    "getUserInvitation",
    predicate,
    unauthed
  );
}

export const fetchUserInvitationsByProgramIds = async (
  programIds: string[]
) => {
  if (!programIds?.length) {
    return [];
  }
  const predicate =
    programIds.length === 1
      ? { programId: programIds[0] }
      : { programIds };
  return await listQueryAutoPaginated<
    ListUserInvitationsQuery,
    UserInvitation,
    ModelUserInvitationConnection
  >(
    listUserInvitationsByProgramIds(programIds),
    "listUserInvitations",
    "items",
    predicate
  );
};

export const createUserInvitation = async (
  input: CreateUserInvitationInput
): Promise<UserInvitation | null> => {
  const predicate = { input: input };
  return await genericQuery<CreateUserInvitationMutation, UserInvitation>(
    mutations.createUserInvitation,
    "createUserInvitation",
    predicate
  );
};

export const deleteUserInvitation = async (
  input: DeleteUserInvitationInput
): Promise<UserInvitation | null> => {
  const predicate = { input: input };
  return await genericQuery<DeleteUserInvitationMutation, UserInvitation>(
    mutations.deleteUserInvitation,
    "deleteUserInvitation",
    predicate
  );
};

export const fetchEulaText = async (): Promise<EULAText | null> => {
  try {
    // Use REST API endpoint /eula-texts which returns the list ordered by createdAt desc
    const response = await API.get("simpl-api", "eula-texts", {});
    
    if (response?.eulatexts && Array.isArray(response.eulatexts) && response.eulatexts.length > 0) {
      // The endpoint already returns results ordered by createdAt descending
      // Return the first (newest) item
      const newest = response.eulatexts[0];
      return {
        id: newest.id,
        formattedText: newest.formattedText,
        createdAt: newest.createdAt,
        updatedAt: newest.updatedAt
      } as EULAText;
    }
    return null;
  } catch (error) {
    console.error("Error fetching EULA text via REST API:", error);
    throw error;
  }
};

export type ValidateBulkUploadResult = {
  success: boolean;
  invitations?: Array<{
    requestId?: string;
    firstName: string;
    lastName: string;
    email: string;
    role: string;
    programId?: string;
    npi?: string | number;
    simplLearnId?: string;
    pgy?: string | number;
    yearInStage?: string | number;
    traineeType?: string;
    jobTitle?: string;
    errors?: Array<{ field: string; error: string }>;
  }>;
  error?: string;
}

// The WAF in front of api.simpl.org runs AWSManagedRulesCommonRuleSet, whose
// SizeRestrictions_BODY rule blocks any request body over 8 KB with a 403 (before
// it reaches the Lambda, so the error has no CORS headers and surfaces as a CORS
// error). A bulk list serializes to ~280 bytes/row, so ~30 rows already crosses
// 8 KB. Split into size-bounded batches so each request stays well under the limit,
// then merge the per-batch results. Target is conservative to leave header/wrapper
// headroom under the 8192-byte ceiling.
const VALIDATE_BATCH_MAX_BYTES = 6000;

const byteLength = (s: string): number =>
  typeof TextEncoder !== "undefined"
    ? new TextEncoder().encode(s).length
    : unescape(encodeURIComponent(s)).length;

const chunkInvitationsBySize = (
  invitations: ValidateBulkUploadInvitation[]
): ValidateBulkUploadInvitation[][] => {
  const batches: ValidateBulkUploadInvitation[][] = [];
  let current: ValidateBulkUploadInvitation[] = [];
  let currentBytes = byteLength(`{"invitations":[]}`);
  for (const inv of invitations) {
    const invBytes = byteLength(JSON.stringify(inv)) + 1; // +1 for the joining comma
    if (current.length > 0 && currentBytes + invBytes > VALIDATE_BATCH_MAX_BYTES) {
      batches.push(current);
      current = [];
      currentBytes = byteLength(`{"invitations":[]}`);
    }
    current.push(inv);
    currentBytes += invBytes;
  }
  if (current.length > 0) batches.push(current);
  return batches;
};

export const fetchValidateBulkUpload = async (
  invitations: ValidateBulkUploadInvitation[]
): Promise<ValidateBulkUploadResult | null> => {
  if (!invitations.length) return { success: true, invitations: [] };

  const batches = chunkInvitationsBySize(invitations);
  const merged: NonNullable<ValidateBulkUploadResult["invitations"]> = [];
  // Sequential so a failure (e.g. the batch is still somehow blocked) rejects the
  // whole validation — callers must treat any failure as "not validated" (fail closed),
  // never as "all rows valid".
  for (const batch of batches) {
    const response = (await API.post("simpl-api", "validate-bulk-upload", {
      body: { invitations: batch }
    })) as ValidateBulkUploadResult;
    // Fail closed: any unsuccessful/errored batch, or one that didn't return a
    // result for every row, must abort the whole validation. Never silently drop
    // rows and report success — the caller would mark unvalidated rows as ready.
    if (!response || response.success !== true || response.error) {
      throw new Error(response?.error || "Bulk upload validation failed");
    }
    if (
      !Array.isArray(response.invitations) ||
      response.invitations.length !== batch.length
    ) {
      throw new Error(
        "Bulk upload validation returned an incomplete response (missing results for some rows)."
      );
    }
    merged.push(...response.invitations);
  }
  return { success: true, invitations: merged };
};

export interface CreateAuthAccountInput {
  email: string;
  password: string;
  permissions: string;
}
export async function createCognitoAccount(input: CreateAuthAccountInput) {
  const signupProps = {
    // This probably won't be needed after the other changes, but just to be sure.
    username: input.email.toLowerCase(),
    password: input.password,
    attributes: {
      email: input.email,
      "custom:permissions": input.permissions
    }
  };
  const { user } = await Auth.signUp({
    ...signupProps,
    autoSignIn: {
      enabled: true
    }
    // DEV NOTE: Auto-sign in will be handled in a future ticket.
    // autoSignIn: { enabled: true }
  });
  return user;
}

export interface ConfirmCognitoAccountInput {
  username: string;
  code: string;
}

export async function confirmCognitoAccount(input: ConfirmCognitoAccountInput) {
  const { username, code } = input;
  return await Auth.confirmSignUp(username, code);
}

export const resendConfirmationCode = async (username: string) => {
  return await Auth.resendSignUp(username);
};

export const resendSignUp = async (username: string) => {
  return await Auth.resendSignUp(username);
};

export const searchUserInvitations = async (
  predicate: SearchUserInvitationsQueryVariables
) => {
  const results = await searchQuery<
    SearchUserInvitationsQuery,
    SearchableUserInvitationConnection
  >(queries.searchUserInvitations, "searchUserInvitations", predicate);
  return results;
};
